import React, { useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

export default function StateDistribution({ clients }) {
  const [viewMode, setViewMode] = useState('status') // "status" or "company"
  const [displayLimit, setDisplayLimit] = useState('10') // "10", "20", or "all"

  const getClientCountByStateStatus = () => {
    const clientCountByState = clients.reduce((acc, client) => {
      const state = client.state || 'Unknown'
      if (!acc[state]) {
        acc[state] = { active: 0, inactive: 0, done: 0, total: 0 }
      }
      
      if (client.status === 'Active') acc[state].active += 1
      else if (client.status === 'Inactive') acc[state].inactive += 1
      else if (client.status === 'Done') acc[state].done += 1
      
      acc[state].total += 1
      return acc
    }, {})

    const sortedData = Object.entries(clientCountByState).map(([state, counts]) => ({
      name: state,
      active: counts.active,
      inactive: counts.inactive,
      done: counts.done,
      total: counts.total
    })).sort((a, b) => b.total - a.total)

    // Apply display limit
    if (displayLimit === 'all') return sortedData
    return sortedData.slice(0, parseInt(displayLimit))
  }

  const getClientCountByStateCompany = () => {
    const clientCountByState = clients.reduce((acc, client) => {
      const state = client.state || 'Unknown'
      if (!acc[state]) {
        acc[state] = { SMG: 0, DCM: 0, DRM: 0, total: 0 }
      }
      
      if (client.company === 'SMG') acc[state].SMG += 1
      else if (client.company === 'DCM') acc[state].DCM += 1
      else if (client.company === 'DRM') acc[state].DRM += 1
      
      acc[state].total += 1
      return acc
    }, {})

    const sortedData = Object.entries(clientCountByState).map(([state, counts]) => ({
      name: state,
      SMG: counts.SMG,
      DCM: counts.DCM,
      DRM: counts.DRM,
      total: counts.total
    })).sort((a, b) => b.total - a.total)

    // Apply display limit
    if (displayLimit === 'all') return sortedData
    return sortedData.slice(0, parseInt(displayLimit))
  }

  const statusData = getClientCountByStateStatus()
  const companyData = getClientCountByStateCompany()

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#111827',
            margin: 0
          }}>
            Number of Clients by State
          </h3>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <select
              value={displayLimit}
              onChange={(e) => setDisplayLimit(e.target.value)}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'white',
                minWidth: '120px'
              }}
            >
              <option value="10">Show Top 10</option>
              <option value="20">Show Top 20</option>
              <option value="all">Show All</option>
            </select>
            <div style={{
              display: 'flex',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              padding: '4px'
            }}>
              <button
                onClick={() => setViewMode('status')}
                style={{
                  backgroundColor: viewMode === 'status' ? 'white' : 'transparent',
                  color: viewMode === 'status' ? '#111827' : '#6b7280',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: viewMode === 'status' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
                }}
              >
                By Status
              </button>
              <button
                onClick={() => setViewMode('company')}
                style={{
                  backgroundColor: viewMode === 'company' ? 'white' : 'transparent',
                  color: viewMode === 'company' ? '#111827' : '#6b7280',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  boxShadow: viewMode === 'company' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
                }}
              >
                By Company
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={viewMode === 'status' ? statusData : companyData}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2}/>
            <XAxis dataKey="name" tick={{ fill: '#6b7280' }} fontSize={12} />
            <YAxis tick={{ fill: '#6b7280' }} fontSize={12} />
            <Tooltip 
              formatter={(value, name) => [value, name]}
              labelFormatter={(label) => label}
              contentStyle={{ 
                backgroundColor: 'rgba(31, 41, 55, 0.9)', 
                backdropFilter: 'blur(4px)', 
                border: '1px solid #4b5563', 
                borderRadius: '8px',
                color: '#f3f4f6'
              }}
              cursor={{ fill: '#f3f4f6', fillOpacity: 0.2 }}
            />
            {viewMode === 'status' ? (
              <>
                <Bar dataKey="active" stackId="a" fill="#22C55E" name="Active Clients" />
                <Bar dataKey="inactive" stackId="a" fill="#6B7280" name="Inactive Clients" />
                <Bar dataKey="done" stackId="a" fill="#3B82F6" name="Done Clients" />
              </>
            ) : (
              <>
                <Bar dataKey="SMG" stackId="a" fill="#FFFF00" name="SMG Clients" />
                <Bar dataKey="DCM" stackId="a" fill="#000000" name="DCM Clients" />
                <Bar dataKey="DRM" stackId="a" fill="#858585" name="DRM Clients" />
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}